import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import React, { useEffect, useMemo, useRef, useState, Fragment } from "react";
import { get, post } from "../../../utils/api";
import BulkFileUpload from "../../Scope1/FugitiveEmmissions/BulkFileUpload";
import AddCustomerMaster from "../../Masters/Scope3/CustomerMaster/AddCustomerMaster";

const VENDOR_KEY = "Customer/Vendor Name";
const PRODUCT_ID_EXCEL_KEY = "Product ID / SKU";
const BULK_UPLOAD_JSON_SNACK_DURATION = 4000;
const ADD_NEW_CUSTOMER_VALUE = "add_new";

/** Same field config as `CustomerMaster.js` for AddCustomerMaster */
const CUSTOMER_MASTER_FORM_INPUT = [
  {
    fieldName: "customerVendorName",
    title: "Customer/Vendor Name",
    placeholder: "Enter Customer/Vendor Name",
  },
  { fieldName: "productName", title: "Product Name" },
  {
    fieldName: "picName",
    title: "PIC Name",
    placeholder: "Enter PIC Name",
  },
  { fieldName: "country", title: "Country" },
  {
    fieldName: "email",
    title: "Email",
    placeholder: "Enter Email",
  },
  {
    fieldName: "processingMethodsUsed",
    title: "Processing methods used",
    placeholder: "Enter method",
  },
];

/** Bulk row context: these keys are prefilled from the upload and stay non-editable in AddCustomerMaster. */
const BULK_ADD_CUSTOMER_READ_ONLY_FIELD_NAMES = [
  "customerVendorName",
  "productName",
];

const normalizeProcessingMethodsUsed = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string" && raw.trim()) {
    return raw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
};

const getEnteredVendor = (row) => {
  if (!row) return "";
  const v = row.vendorName ?? row.customerVendorName ?? row[VENDOR_KEY];
  return typeof v === "string" ? v.trim() : String(v || "").trim();
};

const getExcelProductId = (dataRow, matchRow) => {
  const raw =
    dataRow?.[PRODUCT_ID_EXCEL_KEY] ??
    matchRow?.[PRODUCT_ID_EXCEL_KEY] ??
    dataRow?.productId ??
    matchRow?.productId ??
    "";
  return String(raw || "").trim();
};

const findProductForRow = (productsWithCustomers, dataRow, matchRow) => {
  const pid = getExcelProductId(dataRow, matchRow);
  if (!pid || !Array.isArray(productsWithCustomers)) return null;
  return (
    productsWithCustomers.find(
      (p) => String(p?.productId || "").trim() === pid
    ) || null
  );
};

const isProductUnmatched = (row) => row && row.productMatched === false;
const isCustomerUnmatched = (row) => row && row.customerMatched === false;

const hasValidCustomerSelection = (formRows, index) => {
  const selected = String(formRows?.[index]?.customerMasterId || "").trim();
  return Boolean(selected) && selected !== ADD_NEW_CUSTOMER_VALUE;
};

const BulkUploadReview = ({
  matchData = [],
  bulkUploadTable,
  onClose,
  onConfirm,
  formDataBulkUpload = [],
  setFormDataBulkUpload,
  setsnackOpen,
  bulkUploadUrl,
}) => {
  const [productsWithCustomers, setProductsWithCustomers] = useState([]);
  const [loadingMasters, setLoadingMasters] = useState(false);
  const [finalJson, setFinalJson] = useState(formDataBulkUpload);
  const [uploadValidationErrors, setUploadValidationErrors] = useState([]);
  const [hasValidationBeenAttempted, setHasValidationBeenAttempted] =
    useState(false);
  const [documentUploadConfirmed, setDocumentUploadConfirmed] = useState(false);
  const [documentUploadType, setDocumentUploadType] = useState("none");
  const [commonDocument, setCommonDocument] = useState(null);
  const [individualDocuments, setIndividualDocuments] = useState({});
  const [showNoDocumentDialog, setShowNoDocumentDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [skippedPreview, setSkippedPreview] = useState([]);

  const [addCustomerRowIndex, setAddCustomerRowIndex] = useState(null);
  const [addCustomerElement, setAddCustomerElement] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const pendingAddContextRef = useRef({
    rowIndex: null,
    productMasterId: null,
    savedVendorName: null,
  });

  useEffect(() => {
    if (Array.isArray(formDataBulkUpload)) setFinalJson(formDataBulkUpload);
  }, [formDataBulkUpload]);

  useEffect(() => {
    const fetchMasters = async () => {
      setLoadingMasters(true);
      try {
        const res = await get("/master/customerMasters/products-with-customers");
        const list = Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res?.data)
            ? res.data
            : [];
        setProductsWithCustomers(list);
      } catch (e) {
        setProductsWithCustomers([]);
      } finally {
        setLoadingMasters(false);
      }
    };
    if (bulkUploadTable) fetchMasters();
  }, [bulkUploadTable]);

  /** Stamp product master fields from Product ID / SKU (no product dropdown). */
  useEffect(() => {
    if (!bulkUploadTable || !productsWithCustomers.length) return;
    if (!Array.isArray(matchData) || matchData.length === 0) return;
    setFormDataBulkUpload((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      let changed = false;
      const next = prev.map((dataRow, idx) => {
        const matchRow = matchData[idx];
        const product = findProductForRow(
          productsWithCustomers,
          dataRow,
          matchRow
        );
        if (!product) return dataRow;
        if (dataRow.productMasterId === product._id) return dataRow;
        changed = true;
        return {
          ...dataRow,
          productMasterId: product._id,
          productId: product.productId || dataRow.productId,
          productName: product.productName || dataRow.productName,
          productCategory: product.category || dataRow.productCategory,
        };
      });
      return changed ? next : prev;
    });
  }, [bulkUploadTable, productsWithCustomers, matchData, setFormDataBulkUpload]);

  const applyCustomerToRow = (rowIndex, customer) => {
    if (!customer || rowIndex == null) return;
    setFormDataBulkUpload((prev) => {
      const arr = Array.isArray(prev) ? [...prev] : [];
      const existing = { ...(arr[rowIndex] || {}) };
      arr[rowIndex] = {
        ...existing,
        customerMasterId: customer._id,
        vendorName: customer.customerVendorName || existing.vendorName,
        locationOfProcessing: customer.country || existing.locationOfProcessing,
        processingMethodUsed: normalizeProcessingMethodsUsed(
          customer.processingMethodsUsed
        ),
      };
      return arr;
    });
  };

  const refetchMastersAndLinkCustomer = async () => {
    const res = await get("/master/customerMasters/products-with-customers");
    const list = Array.isArray(res?.data?.data)
      ? res.data.data
      : Array.isArray(res?.data)
        ? res.data
        : [];
    setProductsWithCustomers(list);
    const ctx = pendingAddContextRef.current;
    const rowIndex = ctx.rowIndex;
    const productMasterId = ctx.productMasterId;
    const savedName = (ctx.savedVendorName || "").trim().toLowerCase();
    if (rowIndex == null || !productMasterId || !savedName) return;
    const product = list.find((p) => p._id === productMasterId);
    const customer = (product?.customers || []).find(
      (c) => (c.customerVendorName || "").trim().toLowerCase() === savedName
    );
    if (customer) {
      applyCustomerToRow(rowIndex, customer);
      setExpandedRows((prev) => ({ ...prev, [rowIndex]: false }));
      setAddCustomerRowIndex(null);
    }
  };

  const setCustomerForRow = (index, customerMasterId) => {
    setFormDataBulkUpload((prev) => {
      const arr = Array.isArray(prev) ? [...prev] : [];
      const existing = { ...(arr[index] || {}) };
      const product = findProductForRow(
        productsWithCustomers,
        existing,
        matchData[index]
      );
      const customer = (product?.customers || []).find(
        (c) => c._id === customerMasterId
      );
      const fallbackVendor =
        getEnteredVendor(existing) || getEnteredVendor(matchData[index]);
      if (!customer) {
        arr[index] = {
          ...existing,
          customerMasterId: "",
          vendorName: fallbackVendor,
        };
        return arr;
      }
      arr[index] = {
        ...existing,
        customerMasterId: customer._id,
        vendorName: customer.customerVendorName || "",
        locationOfProcessing: customer.country || "",
        processingMethodUsed: normalizeProcessingMethodsUsed(
          customer.processingMethodsUsed
        ),
      };
      return arr;
    });
    setExpandedRows((prev) => ({ ...prev, [index]: false }));
  };

  const getCustomerOptionsForIndex = (index) => {
    const dataRow = formDataBulkUpload?.[index];
    const product = findProductForRow(
      productsWithCustomers,
      dataRow,
      matchData?.[index]
    );
    return product?.customers || [];
  };

  const prepareAddCustomerForRow = (index) => {
    const dataRow = formDataBulkUpload?.[index];
    const matchRow = matchData?.[index];
    const product = findProductForRow(
      productsWithCustomers,
      dataRow,
      matchRow
    );
    if (!product?._id) {
      setsnackOpen?.({
        open: true,
        message:
          "Could not resolve product for this row. Check Product ID / SKU exists in masters.",
        severity: "error",
      });
      return;
    }
    const vendor = getEnteredVendor(dataRow) || getEnteredVendor(matchRow);
    setFormDataBulkUpload((prev) => {
      const arr = Array.isArray(prev) ? [...prev] : [];
      arr[index] = {
        ...(arr[index] || {}),
        customerMasterId: ADD_NEW_CUSTOMER_VALUE,
        vendorName: vendor,
      };
      return arr;
    });
    pendingAddContextRef.current = {
      rowIndex: index,
      productMasterId: product._id,
      savedVendorName: null,
    };
    setAddCustomerElement({
      customerVendorName: vendor,
      productName: product._id,
    });
    setAddCustomerRowIndex(index);
    setExpandedRows({ [index]: true });
  };

  const disableConfirm = useMemo(() => {
    if (!Array.isArray(matchData) || matchData.length === 0) return true;
    return matchData.some((row, idx) => {
      if (isProductUnmatched(row)) return true;
      if (isCustomerUnmatched(row)) {
        return !hasValidCustomerSelection(formDataBulkUpload, idx);
      }
      return false;
    });
  }, [matchData, formDataBulkUpload]);

  const handleDocumentUploadConfirmation = (confirmed) => {
    setDocumentUploadConfirmed(confirmed);
    if (!confirmed) {
      setDocumentUploadType("none");
      setCommonDocument(null);
      setIndividualDocuments({});
      setFinalJson((prev) =>
        Array.isArray(prev)
          ? prev.map((entry) => ({ ...entry, "Document (optional)": null }))
          : prev
      );
    }
    setUploadValidationErrors([]);
    setHasValidationBeenAttempted(false);
  };

  const handleDocumentUploadTypeChange = (event) => {
    setDocumentUploadType(event.target.value);
    setCommonDocument(null);
    setIndividualDocuments({});
    setUploadValidationErrors([]);
    setHasValidationBeenAttempted(false);
  };

  const handleCommonDocumentChange = (files) => {
    setCommonDocument(files);
    setFormDataBulkUpload((prev) =>
      Array.isArray(prev)
        ? prev.map((entry) => ({ ...entry, "Document (optional)": files }))
        : prev
    );
    if (Array.isArray(finalJson) && finalJson.length > 0) {
      setFinalJson(finalJson.map((entry) => ({ ...entry, "Document (optional)": files })));
    }
    if (files && files.length > 0) {
      setUploadValidationErrors([]);
      setHasValidationBeenAttempted(false);
    }
  };

  const handleCommonDocumentClear = () => {
    setCommonDocument(null);
    setFormDataBulkUpload((prev) =>
      Array.isArray(prev)
        ? prev.map((entry) => ({ ...entry, "Document (optional)": null }))
        : prev
    );
    if (Array.isArray(finalJson) && finalJson.length > 0) {
      setFinalJson(finalJson.map((entry) => ({ ...entry, "Document (optional)": null })));
    }
  };

  const handleIndividualDocumentChange = (index, files) => {
    setIndividualDocuments((prev) => ({ ...prev, [index]: files }));
    setFormDataBulkUpload((prev) =>
      Array.isArray(prev)
        ? prev.map((entry, i) =>
          i === index ? { ...entry, "Document (optional)": files } : entry
        )
        : prev
    );
    if (Array.isArray(finalJson) && finalJson.length > 0) {
      setFinalJson(
        finalJson.map((entry, i) =>
          i === index ? { ...entry, "Document (optional)": files } : entry
        )
      );
    }
    if (files && files.length > 0) {
      setUploadValidationErrors([]);
      setHasValidationBeenAttempted(false);
    }
  };

  const handleIndividualDocumentClear = (index) => {
    setIndividualDocuments((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    setFormDataBulkUpload((prev) =>
      Array.isArray(prev)
        ? prev.map((entry, i) =>
          i === index ? { ...entry, "Document (optional)": null } : entry
        )
        : prev
    );
    if (Array.isArray(finalJson) && finalJson.length > 0) {
      setFinalJson(
        finalJson.map((entry, i) =>
          i === index ? { ...entry, "Document (optional)": null } : entry
        )
      );
    }
  };

  const validateUploadData = () => {
    const validationErrors = [];
    if (documentUploadConfirmed && documentUploadType === "none") {
      validationErrors.push(
        "Please select a document upload type when document upload is confirmed."
      );
    }
    return validationErrors;
  };

  const performUpload = async () => {
    try {
      setUploading(true);
      setSkippedPreview([]);
      const dataToUse =
        Array.isArray(finalJson) && finalJson.length > 0
          ? finalJson
          : formDataBulkUpload;
      const uploadData = {
        excelData: dataToUse,
        documentUploadType,
        commonDocument,
        individualDocuments,
      };
      const res = await post(bulkUploadUrl, uploadData);
      const inserted = Number(res?.data?.inserted ?? 0);
      const skipped = Number(res?.data?.skipped ?? 0);

      if (inserted === 0) {
        setSkippedPreview(res?.data?.skippedEntries || []);
        setsnackOpen?.({
          open: true,
          message:
            skipped > 0
              ? `No data inserted. ${skipped} row${skipped > 1 ? "s were" : " was"} skipped. Please review and try again.`
              : res?.data?.message ||
              "No data inserted. Please review and try again.",
          severity: "error",
          autoHideDuration: BULK_UPLOAD_JSON_SNACK_DURATION,
        });
        return;
      }

      const responseData = res?.data ?? {};
      const skippedCount = Number(responseData?.skipped ?? skipped ?? 0);
      const apiMessage = responseData?.message;
      setsnackOpen?.({
        open: true,
        message:
          skippedCount > 0
            ? apiMessage ||
            `${inserted} entr${inserted === 1 ? "y" : "ies"} inserted. ${skippedCount} row${skippedCount === 1 ? "" : "s"} skipped.`
            : apiMessage || "Uploaded successfully",
        severity: skippedCount > 0 ? "warning" : "success",
        autoHideDuration: BULK_UPLOAD_JSON_SNACK_DURATION,
      });

      onConfirm?.(responseData);
      onClose?.();
    } catch (e) {
      const errMessage = e?.response?.data?.details
        ?.map((el) => el?.error || el?.reason || el?.msg)
        ?.filter(Boolean)
        ?.join(", ");
      setsnackOpen?.({
        open: true,
        message: errMessage || e?.response?.data?.message || "Upload failed",
        severity: "error",
        autoHideDuration: BULK_UPLOAD_JSON_SNACK_DURATION,
      });
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmUpload = async () => {
    setHasValidationBeenAttempted(true);
    const validationErrors = validateUploadData();
    if (validationErrors.length > 0) {
      setUploadValidationErrors(validationErrors);
      return;
    }
    const needsDocs = documentUploadConfirmed && documentUploadType !== "none";
    if (needsDocs) {
      const hasCommonDoc =
        documentUploadType === "same" &&
        commonDocument &&
        commonDocument.length > 0;
      const hasIndividualDocs =
        documentUploadType === "individual" &&
        Object.keys(individualDocuments).length > 0 &&
        Object.values(individualDocuments).some((docs) => docs && docs.length > 0);
      if (!hasCommonDoc && !hasIndividualDocs) {
        setShowNoDocumentDialog(true);
        return;
      }
    }
    await performUpload();
  };

  if (!bulkUploadTable) return null;

  return (
    <>
      <Dialog
        open={bulkUploadTable}
        onClose={onClose}
        maxWidth="lg"
        sx={{
          "& .MuiDialog-paper": {
            width: "85%",
            margin: "16px",
            maxHeight: "calc(100% - 32px)",
            zIndex: 1000,
          },
        }}
      >
        <DialogTitle>Review Bulk Upload Data</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Review matched and unmatched rows. Select a valid customer for
            unmatched records (or add one in masters) before confirming upload.
          </Typography>

          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", width: "30%" }}>
                    Data Entered
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: "30%" }}>
                    Master Data
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: "20%" }}>
                    Action
                  </TableCell>
                  {documentUploadConfirmed && documentUploadType === "individual" && (
                    <TableCell sx={{ fontWeight: "bold", width: "20%" }}>
                      Document
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    Product ID / SKU
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} />
                  {documentUploadConfirmed && documentUploadType === "individual" && (
                    <TableCell sx={{ fontWeight: "bold" }} />
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {(matchData || []).map((row, index) => {
                  const dataRow = formDataBulkUpload?.[index] || {};
                  const vendor =
                    getEnteredVendor(dataRow) || getEnteredVendor(row);
                  const excelPid = getExcelProductId(dataRow, row);
                  const productResolved = findProductForRow(
                    productsWithCustomers,
                    dataRow,
                    row
                  );
                  const prodUnmatched = isProductUnmatched(row);
                  const custUnmatched = isCustomerUnmatched(row);
                  const userLinkedCustomer = hasValidCustomerSelection(
                    formDataBulkUpload,
                    index
                  );
                  /** Server match or user picked / added customer and row was linked in form state */
                  const masterMatchedChip =
                    !prodUnmatched &&
                    (row.customerMatched === true || userLinkedCustomer);
                  const customerOpts = getCustomerOptionsForIndex(index);
                  const selectedCustomerId = dataRow.customerMasterId || "";

                  return (
                    <Fragment key={index}>
                      <TableRow>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {excelPid || "-"}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                          >
                            {vendor || "-"}
                          </Typography>
                          {!productResolved && !loadingMasters && (
                            <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
                              No product master for this Product ID / SKU.
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {prodUnmatched ? (
                            <Typography variant="body2" color="error">
                              Fix product in masters or correct the file.
                            </Typography>
                          ) : masterMatchedChip ? (
                            <Chip label="Matched" color="success" size="small" />
                          ) : custUnmatched ? (
                            <Stack spacing={1} sx={{ minWidth: "10.5rem", maxWidth: "100%" }}>
                              <FormControl
                                size="small"
                                sx={{ minWidth: "10.5rem", maxWidth: "10.5rem" }}
                              >
                                <Select
                                  value={selectedCustomerId || ""}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    if (v === ADD_NEW_CUSTOMER_VALUE) {
                                      prepareAddCustomerForRow(index);
                                      return;
                                    }
                                    setCustomerForRow(index, v);
                                  }}
                                  fullWidth
                                  displayEmpty
                                  disabled={loadingMasters || !productResolved}
                                >
                                  <MenuItem value="" disabled>
                                    Tap to Select
                                  </MenuItem>
                                  <MenuItem value={ADD_NEW_CUSTOMER_VALUE}>
                                    Add New Customer +
                                  </MenuItem>
                                  {customerOpts.map((c) => (
                                    <MenuItem key={c._id} value={c._id}>
                                      {c.customerVendorName || c._id}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {masterMatchedChip ? null : prodUnmatched ? (
                            <Chip label="Unmatched" color="warning" size="small" />
                          ) : custUnmatched ? (
                            expandedRows[index] ? (
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => {
                                  setExpandedRows((prev) => ({
                                    ...prev,
                                    [index]: false,
                                  }));
                                  setAddCustomerRowIndex(null);
                                  setAddCustomerElement(null);
                                }}
                                sx={{ minWidth: "80px" }}
                              >
                                Close
                              </Button>
                            ) : hasValidCustomerSelection(formDataBulkUpload, index) ? (
                              <Chip
                                label="Selected"
                                color="info"
                                size="small"
                              />
                            ) : (
                              <Chip label="Unmatched" color="warning" size="small" />
                            )
                          ) : (
                            <Chip label="Unmatched" color="warning" size="small" />
                          )}
                        </TableCell>
                        {documentUploadConfirmed && documentUploadType === "individual" && (
                          <TableCell>
                            <BulkFileUpload
                              handleChange={handleIndividualDocumentChange}
                              handleClear={handleIndividualDocumentClear}
                              filesList={
                                individualDocuments[index] ||
                                formDataBulkUpload?.[index]?.["Document (optional)"] ||
                                finalJson?.[index]?.["Document (optional)"] ||
                                []
                              }
                              index={index}
                            />
                          </TableCell>
                        )}
                      </TableRow>
                      <TableRow>
                        <TableCell
                          style={{ paddingBottom: 0, paddingTop: 0 }}
                          colSpan={
                            documentUploadConfirmed && documentUploadType === "individual"
                              ? 4
                              : 3
                          }
                        >
                          <Collapse in={Boolean(expandedRows[index])} timeout="auto" unmountOnExit>
                            <Box sx={{ mt: 2 }}>
                              <AddCustomerMaster
                                key={`bulk-add-customer-${index}`}
                                id=""
                                addData={Boolean(expandedRows[index])}
                                setAddData={(next) => {
                                  if (!next) {
                                    setExpandedRows((prev) => ({
                                      ...prev,
                                      [index]: false,
                                    }));
                                    setAddCustomerRowIndex(null);
                                    setAddCustomerElement(null);
                                  }
                                }}
                                element={
                                  addCustomerRowIndex === index ? addCustomerElement : null
                                }
                                addUrl="/master/customerMasters"
                                updateUrl="/master/customerMasters"
                                fetchData={refetchMastersAndLinkCustomer}
                                setsnackOpen={setsnackOpen}
                                formInputData={CUSTOMER_MASTER_FORM_INPUT}
                                onAfterSave={({ payload }) => {
                                  pendingAddContextRef.current.savedVendorName =
                                    payload?.customerVendorName?.trim() || null;
                                }}
                                embedded
                                embeddedTitle={`Add Customer`}
                                embeddedRootSx={{
                                  display: "flex",
                                  flexDirection: "column",
                                }}
                                embeddedFieldsContainerSx={{
                                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                                  columnGap: 2,
                                  rowGap: 1,
                                }}
                                fieldContainerSxByFieldName={{
                                  processingMethodsUsed: {
                                    // border: "2px solid red",
                                    width: "50%"
                                  },
                                }}
                                // submitButtonContainerSx={{
                                //   marginTop: "1.5rem",
                                //   marginBottom: "1rem",
                                // }}
                                readOnlyFieldNames={BULK_ADD_CUSTOMER_READ_ONLY_FIELD_NAMES}
                              />
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              mt: 3,
              p: 2,
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              width: "100%",
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
              Document Upload
            </Typography>
            <Box sx={{ width: "100%" }}>
              <FormControl
                component="fieldset"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  width: "100%",
                  m: 0,
                }}
              >
                <FormLabel
                  component="legend"
                  sx={{ mb: 1, color: "text.primary", fontSize: "0.875rem" }}
                >
                  Do you want to upload document?
                </FormLabel>
                <RadioGroup
                  value={documentUploadConfirmed ? "yes" : "no"}
                  onChange={(e) =>
                    handleDocumentUploadConfirmation(e.target.value === "yes")
                  }
                  row
                >
                  <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                </RadioGroup>
              </FormControl>
            </Box>

            {documentUploadConfirmed && (
              <Box
                sx={{
                  width: "100%",
                  mt: 2,
                  pt: 2,
                  borderTop: "1px solid #e0e0e0",
                }}
              >
                <FormControl
                  component="fieldset"
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    width: "100%",
                    m: 0,
                  }}
                >
                  <FormLabel
                    component="legend"
                    sx={{ mb: 1, color: "text.primary", fontSize: "0.875rem" }}
                  >
                    Document Upload Type
                  </FormLabel>
                  <RadioGroup
                    value={documentUploadType}
                    onChange={handleDocumentUploadTypeChange}
                    row
                    sx={{ flexWrap: "wrap", gap: 0.5 }}
                  >
                    <FormControlLabel
                      value="same"
                      control={<Radio />}
                      label="Same File for All Records"
                    />
                    <FormControlLabel
                      value="individual"
                      control={<Radio />}
                      label="Individual File for Each Record"
                    />
                  </RadioGroup>
                </FormControl>

                {documentUploadType === "same" && (
                  <Box sx={{ mt: 2, width: "100%" }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Upload Common Document:
                    </Typography>
                    <BulkFileUpload
                      handleChange={handleCommonDocumentChange}
                      handleClear={handleCommonDocumentClear}
                      filesList={commonDocument}
                    />
                  </Box>
                )}

                {documentUploadType === "individual" && (
                  <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
                    Upload individual documents for each record. Documents will be
                    added as a new column in the table above.
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {hasValidationBeenAttempted && uploadValidationErrors.length > 0 && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: "#ffebee",
                borderRadius: 1,
                border: "1px solid #f44336",
              }}
            >
              <Typography
                variant="h6"
                color="error"
                sx={{ mb: 1, fontWeight: "bold" }}
              >
                Error
              </Typography>
              {uploadValidationErrors.map((error, i) => (
                <Typography key={i} variant="body2" color="error" sx={{ mb: 0.5 }}>
                  • {error}
                </Typography>
              ))}
            </Box>
          )}

          {skippedPreview.length > 0 && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: "#fff8e1",
                borderRadius: 1,
                border: "1px solid #ffb300",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
                Skipped rows
              </Typography>
              {skippedPreview.map((entry, idx) => (
                <Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
                  •{" "}
                  {typeof entry === "string"
                    ? entry
                    : entry?.reason ||
                    entry?.error ||
                    entry?.message ||
                    JSON.stringify(entry)}
                </Typography>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "1rem" }}>
          <Button
            onClick={onClose}
            variant="outlined"
            color="primary"
            sx={{ marginRight: "1rem" }}
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmUpload}
            variant="contained"
            color="primary"
            disabled={disableConfirm || uploading}
          >
            Confirm Upload
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showNoDocumentDialog}
        onClose={() => setShowNoDocumentDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h6">No documents uploaded</Typography>
            <IconButton
              onClick={() => setShowNoDocumentDialog(false)}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            You chose to upload documents but no files were attached.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Proceed without documents?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: "1rem" }}>
          <Button
            onClick={() => setShowNoDocumentDialog(false)}
            variant="outlined"
            color="primary"
            sx={{ marginRight: "1rem" }}
          >
            Upload documents
          </Button>
          <Button
            onClick={() => {
              setShowNoDocumentDialog(false);
              performUpload();
            }}
            variant="contained"
            color="primary"
          >
            Proceed without documents
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkUploadReview;
